import { ref, computed } from 'vue'
import useAuth from './useAuth'

const selectedAcademy = ref(null)
const contentScope = ref(localStorage.getItem('perform72-contentScope') || 'global')

// Load from localStorage on app start
const storedAcademy = localStorage.getItem('selectedAcademy')
if (storedAcademy) {
  try {
    selectedAcademy.value = JSON.parse(storedAcademy)
  } catch (e) {
    localStorage.removeItem('selectedAcademy')
  }
}

// Listen for localStorage changes (e.g., from logout)
window.addEventListener('storage', e => {
  if (e.key === 'selectedAcademy') {
    if (e.newValue) {
      try {
        selectedAcademy.value = JSON.parse(e.newValue)
      } catch (err) {
        selectedAcademy.value = null
      }
    } else {
      selectedAcademy.value = null
    }
  }

  if (e.key === 'perform72-contentScope') {
    contentScope.value = e.newValue || 'global'
  }
})

export default function useAcademySelection() {
  const { hasRole } = useAuth()

  const isAdmin = computed(() => hasRole(['ADMIN']))

  const canSelectAcademy = computed(() => isAdmin.value)

  const setSelectedAcademy = academy => {
    selectedAcademy.value = academy
    if (academy) {
      localStorage.setItem('selectedAcademy', JSON.stringify(academy))
    } else {
      localStorage.removeItem('selectedAcademy')
    }
  }

  const setContentScope = scope => {
    contentScope.value = scope === 'full' ? 'full' : 'global'
    localStorage.setItem('perform72-contentScope', contentScope.value)
  }

  const clearSelectedAcademy = () => {
    const oldValue = selectedAcademy.value
    selectedAcademy.value = null
    localStorage.removeItem('selectedAcademy')

    // Dispatch storage event to ensure all components react
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new StorageEvent('storage', {
        key: 'selectedAcademy',
        oldValue: oldValue ? JSON.stringify(oldValue) : null,
        newValue: null,
        url: window.location.href,
      }))
    }
  }

  // Check if localStorage was cleared externally (e.g., by logout)
  const checkLocalStorage = () => {
    const stored = localStorage.getItem('selectedAcademy')
    if (!stored && selectedAcademy.value) {
      selectedAcademy.value = null
    }
  }

  const getAcademyHeader = () => {
    if (selectedAcademy.value && selectedAcademy.value.id) {
      return { 'X-Academy-Id': selectedAcademy.value.id }
    }

    return {}
  }

  const getContentScopeParams = () => {
    // The CMS library is an administrative catalogue. With no academy selected,
    // admins must see global and academy-owned content together.
    if (isAdmin.value && !selectedAcademy.value) {
      return { allAcademies: true }
    }

    return {}
  }

  return {
    selectedAcademy: computed(() => selectedAcademy.value),
    contentScope: computed(() => contentScope.value),
    isFullContent: computed(() => contentScope.value === 'full'),
    isAdmin,
    canSelectAcademy,
    setSelectedAcademy,
    setContentScope,
    clearSelectedAcademy,
    checkLocalStorage,
    getAcademyHeader,
    getContentScopeParams,
  }
}
